import React, { useState, useEffect, useCallback, useContext } from 'react';
import apiClient from '../api';
import AuthContext from '../context/AuthContext';
import { formatDate } from '../utils/formatDate';

function AddNoteForm({ offenses = [], onNoteAdded }) { // Added default value for safety
  const [noteText, setNoteText] = useState('');
  const [selectedOffense, setSelectedOffense] = useState('');

  useEffect(() => {
    if (Array.isArray(offenses) && offenses.length > 0) {
      setSelectedOffense(offenses[0].id);
    }
  }, [offenses]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!noteText.trim() || !selectedOffense) return;
    try {
      await apiClient.post(`/api/case/offenses/${selectedOffense}/notes`, { note_text: noteText });
      setNoteText('');
      onNoteAdded();
    } catch (err) {
      console.error("Failed to add note", err);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-4 p-4 border-t">
      <h3 className="text-lg font-semibold text-slate-700 mb-2">Add New Note</h3>
      <div className="space-y-4">
        <div>
          <label htmlFor="offenseSelect" className="block text-sm font-medium text-slate-700 mb-1">For Offense:</label>
          <select
            id="offenseSelect"
            value={selectedOffense}
            onChange={(e) => setSelectedOffense(e.target.value)}
            className="w-full"
            required
            disabled={!offenses || offenses.length === 0}
          >
            {offenses && offenses.map(o => <option key={o.id} value={o.id}>{o.offense_type} ({o.case_number})</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="noteText" className="block text-sm font-medium text-slate-700 mb-1">Note:</label>
          <textarea
            id="noteText"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Add a supplemental report or case note..."
            rows="3"
            className="w-full"
            required
          />
        </div>
        <div className="text-right">
            <button type="submit" className="bg-gold text-white hover:bg-gold-dark font-bold py-2 px-4 rounded-lg shadow-sm">Add Note</button>
        </div>
      </div>
    </form>
  );
}

function CaseNotes({ juvenileId }) {
  const [offenses, setOffenses] = useState([]);
  const [notes, setNotes] = useState([]);
  const [error, setError] = useState('');
  const { hasPermission } = useContext(AuthContext)

  const fetchAllData = useCallback(async () => {
    if (!juvenileId) return;
    try {
      setError('');
      const [offensesRes, notesRes] = await Promise.all([
        apiClient.get(`/api/juveniles/${juvenileId}/offenses`),
        apiClient.get(`/api/case/juveniles/${juvenileId}/notes`)
      ]);
      // **THE FIX IS HERE:** We now access the 'offenses' array inside the response data.
      setOffenses(offensesRes.data.offenses || []);
      setNotes(notesRes.data || []);
    } catch (err) {
      setError('Failed to fetch case notes.');
      console.error(err);
    }
  }, [juvenileId]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  if (error) return <p className="text-red-500">{error}</p>;

  return (
    <div className="card">
      <h2 className="text-xl font-bold text-slate-800">Case Notes & Supplemental Reports</h2>
      <div className="mt-4 space-y-4">
        {notes.length > 0 ? (
          notes.map(note => (
            <div key={note.id} className="p-4 border rounded-lg bg-slate-50">
                <h4 className="font-bold text-slate-700">Offense: {note.offense_type} (Case: {note.case_number})</h4>
                <div className="border-t mt-2 pt-2">
                    <p className="text-slate-800 whitespace-pre-wrap">{note.note_text}</p>
                    <p className="text-xs text-slate-500 mt-2 text-right">
                        <em>By: {note.full_name} on {formatDate(note.created_at)}</em>
                    </p>
                </div>
            </div>
          ))
        ) : <p>No notes for this juvenile.</p>}
      </div>
      {/* Conditionally render the entire 'Add Note' form */}
      {hasPermission('add-case-note') && (
        <AddNoteForm offenses={offenses} onNoteAdded={fetchAllData} />
      )}
    </div>
  );
}

export default CaseNotes;